import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "Главная", href: "#hero" },
  { label: "Преимущества", href: "#advantages" },
  { label: "Партнёры", href: "#partners" },
  { label: "Кейсы", href: "#cases" },
  { label: "Тарифы", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
  { label: "Вакансии", href: "/vacancies", isRoute: true },
];

const TELEGRAM_URL = "https://t.me/Vertttess";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginRole, setLoginRole] = useState<"employee" | "client" | null>(null);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setLoginOpen(false);
        setLoginRole(null);
        setLoginForm({ username: "", password: "" });
        setLoginError("");
      }
    };
    if (loginOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [loginOpen]);

  const handleLoginToggle = () => {
    setLoginOpen((v) => !v);
    setLoginRole(null);
    setLoginForm({ username: "", password: "" });
    setLoginError("");
  };

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginForm.username.trim() || !loginForm.password.trim()) {
      setLoginError("Заполните все поля");
      return;
    }
    setLoginError("Неверный логин или пароль");
  };

  const scrollToAnchor = (hash: string) => {
    const el = document.querySelector(hash);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname === "/") {
      scrollToAnchor(href);
    } else {
      navigate("/");
      setTimeout(() => scrollToAnchor(href), 300);
    }
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/95 backdrop-blur-md shadow-sm"
    >
      <nav className="w-full px-4 md:px-8 lg:px-12 py-4 flex items-center justify-between">
        {/* Logo / Brand */}
        <a
          href="/"
          onClick={(e) => handleAnchorClick(e, "#hero")}
          className="flex items-center gap-3 shrink-0 cursor-pointer"
        >
          <img
            src="/readdy/6606b04b1f7f2f98f392e21c6ab06083.png"
            alt="Have Received"
            className="h-10 w-auto"
          />
          <span className="font-heading text-xl md:text-2xl text-foreground-900 hidden sm:inline">
            Have Received
          </span>
        </a>

        {/* Center Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            )
          )}
        </div>

        {/* Right Buttons */}
        <div className="flex items-center gap-3">
          {/* Personal Account Dropdown */}
          <div className="hidden md:block relative" ref={dropdownRef}>
            <button
              onClick={handleLoginToggle}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-700 border border-foreground-300 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-user-line" />
              Личный кабинет
              {loginOpen ? <i className="ri-arrow-up-s-line text-xs" /> : <i className="ri-arrow-down-s-line text-xs" />}
            </button>

            {loginOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-background-50 border border-background-200 rounded-lg shadow-lg overflow-hidden">
                {!loginRole ? (
                  /* Step 1 — role selection */
                  <div className="p-4">
                    <p className="text-sm font-medium text-foreground-700 mb-3 text-center">
                      Выберите тип входа
                    </p>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => setLoginRole("employee")}
                        className="flex items-center gap-3 px-4 py-3 rounded-md border border-background-200 hover:border-primary-400 hover:bg-primary-50 transition-all cursor-pointer text-left"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                          <i className="ri-briefcase-line text-base" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">Вход для сотрудников</p>
                          <p className="text-xs text-foreground-500">Для работников компании</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-foreground-400 ml-auto" />
                      </button>
                      <button
                        onClick={() => setLoginRole("client")}
                        className="flex items-center gap-3 px-4 py-3 rounded-md border border-background-200 hover:border-accent-400 hover:bg-accent-50 transition-all cursor-pointer text-left"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0">
                          <i className="ri-hotel-line text-base" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">Вход для клиентов</p>
                          <p className="text-xs text-foreground-500">Для отелей и партнёров</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-foreground-400 ml-auto" />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Step 2 — login form */
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <button
                        onClick={() => { setLoginRole(null); setLoginError(""); }}
                        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line text-foreground-600" />
                      </button>
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full ${loginRole === "employee" ? "bg-primary-100 text-primary-600" : "bg-accent-100 text-accent-600"}`}>
                          <i className={`text-xs ${loginRole === "employee" ? "ri-briefcase-line" : "ri-hotel-line"}`} />
                        </div>
                        <p className="text-sm font-medium text-foreground-900">
                          {loginRole === "employee" ? "Вход для сотрудников" : "Вход для клиентов"}
                        </p>
                      </div>
                    </div>

                    <form onSubmit={handleLoginSubmit} className="flex flex-col gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">
                          Логин
                        </label>
                        <input
                          type="text"
                          value={loginForm.username}
                          onChange={(e) => setLoginForm((f) => ({ ...f, username: e.target.value }))}
                          placeholder="Введите логин"
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition"
                          autoComplete="username"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">
                          Пароль
                        </label>
                        <input
                          type="password"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                          placeholder="Введите пароль"
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition"
                          autoComplete="current-password"
                        />
                      </div>
                      {loginError && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <i className="ri-error-warning-line" />
                          {loginError}
                        </p>
                      )}
                      <button
                        type="submit"
                        className="w-full py-2.5 text-sm font-medium bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Войти
                      </button>
                    </form>
                  </div>
                )}
              </div>
            )}
          </div>

          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Оставить заявку
          </a>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-700"
            aria-label="Открыть меню"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200">
          <div className="px-4 py-4 flex flex-col gap-2">
            {navLinks.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="px-3 py-3 text-base font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-md transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className="px-3 py-3 text-base font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-md transition-colors"
                >
                  {link.label}
                </a>
              )
            )}
            {/* Mobile login options */}
            <div className="border-t border-background-200 mt-1 pt-3 flex flex-col gap-2">
              <p className="px-3 text-xs font-medium text-foreground-500 uppercase tracking-wider">
                Личный кабинет
              </p>
              <button
                onClick={() => { setMobileOpen(false); setLoginOpen(true); setLoginRole("employee"); }}
                className="px-3 py-3 text-base font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-md transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-briefcase-line" />
                Вход для сотрудников
              </button>
              <button
                onClick={() => { setMobileOpen(false); setLoginOpen(true); setLoginRole("client"); }}
                className="px-3 py-3 text-base font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-md transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-hotel-line" />
                Вход для клиентов
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}